import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { findGroupForCategory } from "@/data/taxonomy";
import CategoryMultiPicker from "@/components/CategoryMultiPicker";
import LogoUploader from "@/components/LogoUploader";
import HoursEditor from "@/components/HoursEditor";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useTurnstile, TurnstileMount } from "@/components/TurnstileWidget";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isClaim = searchParams.get("claim") !== null;
  const claimEmailHint = searchParams.get("claim_email") || "";
  const [role, setRole] = useState(isClaim ? "business" : "customer");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    house_number: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    birth_date: "",
    business_name: "",
    category: "",
    logo_data_url: "",
    hours: {},
    business_address: "",
    business_city: "",
    business_state: "",
    description: "",
    website: "",
    phone: "",
    contact_email: "",
    date_established: "",
    categories: [],
    wants_promo_emails: true,
  });

  // Pre-fill email if a claim email hint was passed through
  useEffect(() => {
    if (claimEmailHint && !form.email) {
      setForm((f) => ({ ...f, email: claimEmailHint }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimEmailHint]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const invitedBy = searchParams.get("invited_by") || "";
  const [honeypot, setHoneypot] = useState("");
  const turnstile = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, role };
      // Compose a display `name` from first/last so downstream consumers that
      // still read `name` (mask_customer_name, greetings, etc.) keep working.
      const first = (form.first_name || "").trim();
      const last = (form.last_name || "").trim();
      payload.name = `${first} ${last}`.trim();
      if (form.categories?.length) {
        payload.categories = form.categories;
        payload.category_groups = Array.from(new Set(form.categories.map((c) => findGroupForCategory(c) || c)));
        payload.category = form.categories[0];
        payload.category_group = payload.category_groups[0];
      }
      if (invitedBy) payload.invited_by = invitedBy;
      const user = await register(payload, {
        turnstile_token: turnstile.token,
        _hp_website: honeypot,
      });
      toast.success(isClaim ? "Listing claimed" : "Account created");
      if (user.role === "business") {
        // Claimers (and any business signup) flow to subscription so they can unlock the customer ledger
        navigate(isClaim ? "/subscription?from=claim" : "/dashboard/business");
      } else {
        navigate("/dashboard/customer");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      turnstile.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-14">
      <h1 className="font-display text-4xl tracking-tight">{isClaim ? "Claim your listing" : "Create your account"}</h1>
      <p className="mt-2 text-gray-600">
        {isClaim
          ? <>Use the email your listing was submitted under. After verification, you'll be taken to subscribe at <strong>$9.95/mo</strong> to unlock the customer ledger.</>
          : "Pick a side. Both sides earn reputation."}
      </p>

      {isClaim && (
        <div className="mt-5 p-4 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="claim-mode-banner">
          <strong>Claiming an unclaimed listing.</strong> Register with the same email it was submitted under and we'll merge the existing reviews, ratings and listing data onto your new account automatically.
        </div>
      )}

      <Tabs value={role} onValueChange={setRole} className="mt-8">
        <TabsList className="grid grid-cols-2 rounded-sm">
          <TabsTrigger value="customer" className="rounded-sm" data-testid="register-tab-customer">Customer (free)</TabsTrigger>
          <TabsTrigger value="business" className="rounded-sm" data-testid="register-tab-business">Business ($9.95/mo)</TabsTrigger>
        </TabsList>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} className="rounded-sm mt-1" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} className="rounded-sm mt-1" data-testid="register-password-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="first_name">First name</Label>
              <Input
                id="first_name"
                required
                autoComplete="given-name"
                value={form.first_name}
                onChange={update("first_name")}
                className="rounded-sm mt-1"
                data-testid="register-first-name-input"
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last name</Label>
              <Input
                id="last_name"
                required
                autoComplete="family-name"
                value={form.last_name}
                onChange={update("last_name")}
                className="rounded-sm mt-1"
                data-testid="register-last-name-input"
              />
            </div>
          </div>

          <TabsContent value="customer" className="mt-0 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-5">
              <div>
                <Label htmlFor="house_number">House #</Label>
                <Input
                  id="house_number"
                  placeholder="1500"
                  maxLength={20}
                  value={form.house_number}
                  onChange={update("house_number")}
                  className="rounded-sm mt-1 font-mono"
                  data-testid="register-house-number-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="street">Street name</Label>
                <Input id="street" placeholder="Oak Lane" required={role === "customer"} value={form.street} onChange={update("street")} className="rounded-sm mt-1" data-testid="register-street-input" />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" placeholder="Berkeley" required={role === "customer"} value={form.city} onChange={update("city")} className="rounded-sm mt-1" data-testid="register-city-input" />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" placeholder="CA" maxLength={20} value={form.state} onChange={update("state")} className="rounded-sm mt-1" data-testid="register-state-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="zip">ZIP code</Label>
                <Input
                  id="zip"
                  placeholder="94704"
                  inputMode="numeric"
                  maxLength={10}
                  required={role === "customer"}
                  value={form.zip}
                  onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  className="rounded-sm mt-1 font-mono"
                  data-testid="register-zip-input"
                />
              </div>
              <div>
                <Label htmlFor="birth_date">Birth date <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="birth_date"
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={form.birth_date}
                  onChange={update("birth_date")}
                  className="rounded-sm mt-1 font-mono"
                  data-testid="register-birth-date-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Full street address is stored so businesses can send targeted local offers and birthday promos.
              Your house number stays private — profiles publicly show only the street name, city and state.
              Adding your birth date is optional, but unlocks personalised birthday promotions from businesses you follow.
            </p>
          </TabsContent>

          <TabsContent value="business" className="mt-0 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="business_name">Business name</Label>
                <Input id="business_name" required={role === "business"} value={form.business_name} onChange={update("business_name")} className="rounded-sm mt-1" data-testid="register-business-name-input" />
              </div>
              <div>
                <Label>Categories (up to 10)</Label>
                <div className="mt-1">
                  <CategoryMultiPicker
                    value={form.categories || []}
                    onChange={(arr) => setForm({ ...form, categories: arr })}
                    testIdPrefix="register-category"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="business_address">Business street address</Label>
              <Input id="business_address" required={role === "business"} value={form.business_address} onChange={update("business_address")} className="rounded-sm mt-1" data-testid="register-business-address-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="business_city">City</Label>
                <Input id="business_city" placeholder="San Francisco" value={form.business_city} onChange={update("business_city")} className="rounded-sm mt-1" data-testid="register-business-city-input" />
              </div>
              <div>
                <Label htmlFor="business_state">State</Label>
                <Input id="business_state" placeholder="CA" value={form.business_state} onChange={update("business_state")} className="rounded-sm mt-1" data-testid="register-business-state-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={update("phone")} className="rounded-sm mt-1" data-testid="register-phone-input" />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input id="website" type="url" placeholder="https://example.com" value={form.website} onChange={update("website")} className="rounded-sm mt-1" data-testid="register-website-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="contact_email">Public contact email (optional)</Label>
              <Input id="contact_email" type="email" placeholder="hello@example.com" value={form.contact_email} onChange={update("contact_email")} className="rounded-sm mt-1" data-testid="register-contact-email-input" />
              <p className="text-xs text-gray-500 mt-1">Different from your login email — this is shown publicly on your listing.</p>
            </div>
            <div>
              <Label htmlFor="date_established">Year established</Label>
              <Input id="date_established" type="number" min="1850" max="2100" step="1" placeholder="2015" value={form.date_established} onChange={update("date_established")} className="rounded-sm mt-1 max-w-xs" data-testid="register-date-established-input" />
            </div>
            <div>
              <Label htmlFor="description">Short description</Label>
              <Textarea id="description" rows={3} value={form.description} onChange={update("description")} className="rounded-sm mt-1" data-testid="register-description-input" />
            </div>
            <div>
              <Label>Logo <span className="text-gray-400 font-normal">(optional)</span></Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">PNG or JPG, square works best. Auto-resized to 300×300. Skip this and add it later from your dashboard.</p>
              <LogoUploader
                value={form.logo_data_url}
                onChange={(d) => setForm({ ...form, logo_data_url: d })}
              />
            </div>
            <div>
              <Label>Hours of operation <span className="text-gray-400 font-normal">(optional)</span></Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">When are you open? Displayed on your public profile with a live "Open now" badge. You can edit any time.</p>
              <HoursEditor
                value={form.hours}
                onChange={(h) => setForm({ ...form, hours: h })}
              />
            </div>
          </TabsContent>

          {/* Honeypot + Turnstile — same for both role tabs */}
          <input
            type="text"
            name="_hp_website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
            data-testid="register-honeypot"
          />
          <TurnstileMount widgetRef={turnstile.widgetRef} sitekey={turnstile.sitekey} />

          <Button type="submit" className="w-full rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" disabled={loading} data-testid="register-submit-button">
            {loading ? "Creating..." : `Create ${role} account`}
          </Button>
        </form>
      </Tabs>

      <p className="mt-6 text-sm text-gray-600">
        Already a member? <Link to="/login" className="text-[#B31E1E] hover:underline" data-testid="register-link-login">Log in</Link>
      </p>
    </div>
  );
}
